import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api/axios";
import SearchFilter from "../components/SearchFilter";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import Pagination from "../components/Pagination";

export default function ActivityLogs() {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/activity-logs');
      const logsData = response.data.data || [];
      setLogs(logsData);
      setFilteredLogs(logsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs([]);
      setFilteredLogs([]);
      setLoading(false);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLogs = filteredLogs.slice(startIndex, endIndex);

  const handleSearch = (searchTerm) => {
    const filtered = logs.filter(log => 
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.module.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLogs(filtered);
    setCurrentPage(1);
  };

  const handleFilter = async (filterValue) => {
    if (!filterValue) {
      setFilteredLogs(logs);
    } else {
      // Validate filter value to prevent XSS
      const sanitizedFilter = filterValue.replace(/<[^>]*>/g, '').trim();
      if (!sanitizedFilter || !/^[a-zA-Z0-9\s]+$/.test(sanitizedFilter)) {
        console.error('Invalid filter value');
        return;
      }
      
      try {
        const encodedFilter = encodeURIComponent(sanitizedFilter.toLowerCase());
        const response = await api.get(`/activity-logs/${encodedFilter}`);
        const filteredData = response.data.data || [];
        setFilteredLogs(filteredData);
      } catch (error) {
        console.error('Error filtering logs:', error);
        const filtered = logs.filter(log => log.module === sanitizedFilter);
        setFilteredLogs(filtered);
      }
    }
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'timestamp',
      label: t('date_time'),
      render: (value) => new Date(value).toLocaleString(language === 'id' ? 'id-ID' : 'en-US')
    },
    { key: 'action', label: t('action') },
    { key: 'description', label: t('description') },
    { key: 'module', label: t('module') },
    { key: 'user', label: t('user') },
    {
      key: 'type',
      label: t('type'),
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs ${
          value === 'create' ? 'bg-green-100 text-green-800' :
          value === 'update' ? 'bg-blue-100 text-blue-800' :
          value === 'delete' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {value}
        </span>
      )
    }
  ];

  const filters = [
    { value: 'Users', label: t('users') },
    { value: 'Properties', label: t('properties') },
    { value: 'Bookings', label: t('bookings') },
    { value: 'Payments', label: t('payments') }
  ];

  if (loading) {
    return <div className="p-6">{t('loading_activity_logs')}</div>;
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6`}>
        <h1 className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{t('activity_logs')}</h1>
        <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'} mt-1`}>{t('monitor_system_activities')}</p>
      </div>
      
      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <SearchFilter 
            onSearch={handleSearch}
            onFilter={handleFilter}
            filters={filters}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">{t('view')}:</span>
          <button
            onClick={() => setViewMode('cards')}
            className={`px-3 py-2 rounded-lg text-sm transition-all ${
              viewMode === 'cards' 
                ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
                : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
            }`}
          >
            <i className="fas fa-th-large mr-1"></i> {t('cards')}
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-2 rounded-lg text-sm transition-all ${
              viewMode === 'table' 
                ? (isDark ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
                : (isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300')
            }`}
          >
            <i className="fas fa-table mr-1"></i> {t('table')}
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'cards' ? (
        // Cards View
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
          }}
          initial="hidden"
          animate="visible"
        >
          {currentLogs.length > 0 ? (
            currentLogs.map((log, index) => (
              <motion.div 
                key={log.id} 
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 }
                }}
                whileHover={{ scale: 1.05, y: -5 }}
                className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border overflow-hidden hover:shadow-2xl transition-all duration-300`}
              >
                {/* Header */}
                <div className={`p-4 text-white ${
                  log.type === 'create' ? (isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700' : 'bg-gradient-to-r from-blue-500 to-blue-600') :
                  log.type === 'update' ? (isDark ? 'bg-gradient-to-r from-gray-500 to-gray-600' : 'bg-gradient-to-r from-blue-500 to-blue-600') :
                  log.type === 'delete' ? (isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700' : 'bg-gradient-to-r from-red-500 to-red-600') :
                  (isDark ? 'bg-gradient-to-r from-gray-600 to-gray-700' : 'bg-gradient-to-r from-blue-500 to-blue-600')
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <i className={`fas ${
                        log.type === 'create' ? 'fa-plus' :
                        log.type === 'update' ? 'fa-edit' :
                        log.type === 'delete' ? 'fa-trash' : 'fa-cog'
                      } text-lg`}></i>
                    </div>
                    <span className="px-2 py-1 bg-white/20 rounded-full text-xs font-medium">
                      {log.module}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold mt-3">{log.action}</h3>
                  <p className="text-sm opacity-90">{new Date(log.timestamp).toLocaleString(language === 'id' ? 'id-ID' : 'en-US')}</p>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{log.description}</p>
                  <div className={`flex items-center justify-between text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="flex items-center">
                      <i className="fas fa-user mr-1"></i>
                      {log.user}
                    </span>
                    <span className={`px-2 py-1 rounded-full font-medium ${
                      log.type === 'create' ? 'bg-green-100 text-green-800' :
                      log.type === 'update' ? 'bg-blue-100 text-blue-800' :
                      log.type === 'delete' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {log.type}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className={`col-span-full ${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-12 text-center`}>
              <div className="text-6xl mb-4"><i className={`fas fa-clipboard-list ${isDark ? 'text-gray-600' : 'text-gray-300'}`}></i></div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('no_activity_logs_found')}</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('no_system_activities_recorded')}</p>
            </div>
          )}
        </motion.div>
      ) : (
        // Table View
        <div className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border`}>
          <div className="p-6 border-b border-gray-100">
            <h2 className={`text-xl font-bold flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <i className={`fas fa-history mr-3 ${isDark ? 'text-gray-400' : 'text-blue-600'}`}></i>
              {t('system_activity_log')}
            </h2>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 to-blue-50">
                    {columns.map((column) => (
                      <th key={column.key} className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {currentLogs.length > 0 ? (
                    currentLogs.map((log, index) => (
                      <tr key={log.id} className={`hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white/50' : 'bg-slate-50/50'}`}>
                        {columns.map((column) => (
                          <td key={column.key} className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                            {column.render ? column.render(log[column.key]) : log[column.key]}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="px-6 py-12 text-center">
                        <div className="text-6xl mb-4"><i className="fas fa-clipboard-list text-gray-300"></i></div>
                        <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('no_activity_logs_found')}</h3>
                        <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('no_system_activities_recorded')}</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {filteredLogs.length > 0 && (
        <Pagination 
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredLogs.length}
        />
      )}

      {filteredLogs.length === 0 && viewMode === 'cards' && (
        <div className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-12 text-center`}>
          <div className="text-6xl mb-4"><i className={`fas fa-clipboard-list ${isDark ? 'text-gray-600' : 'text-gray-300'}`}></i></div>
          <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('no_activity_logs_found')}</h3>
          <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('try_adjusting_search_filter')}</p>
        </div>
      )}
    </motion.div>
  );
}